<template>
  <div
    class="my-9 d-flex justify-center"
  >
    <div class="en_verb mr-2" v-if="current.en_verb">
      TO
    </div>
    <div class="guess" :style="'width: '+ width + 'px;'">
      <div class="characters">
        <span
          class="character"
          v-for="(char, i) in answer"
          :key="i"
          :class="{ 'no-border': answer[i] === ' ' }"
        >
          {{ guessChar(i) }}
        </span>
      </div>

      <!-- Avoiding v-model due to a bug with gboard -->
      <input
        type="text"
        name="word"
        autocomplete="off"
        :value="guess"
        :maxlength="answer.length"
        v-uppercase
        v-focus
        @input="guess = $event.target.value; type()"
        :disabled="showAnswer"
      />
    </div>
  </div>
</template>

<style scoped>
.en_verb {
  letter-spacing: 8px;
}

.guess {
  display: inline-block;
  position: relative;
  height: 30px;
}

.guess input,
.guess .characters {
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

.guess input {
  background-color: transparent;
  border: 0;
  caret-color: black;
  color: transparent;
  letter-spacing: 17px;
  outline: none;
  padding-left: 8px;
  z-index: 1;
}

.guess .characters {
  z-index: 0;
}

.characters .character {
  border-bottom: 1px solid black;
  display: inline-block;
  margin: 0 2px;
  text-align: center;
  vertical-align: bottom;
  width: 24px;
}
  .characters .character.no-border {
    border: 0;
  }

/* Success and error classes */

.answer-error .guess .characters {
  font-weight: bold;
  color: var(--v-error-base);
}
.answer-error .characters .character {
  border-color: var(--v-error-base);
}

.answer-success .guess .characters {
  font-weight: bold;
  color: var(--v-success-base);
}
.answer-success .characters .character {
  border-color: var(--v-success-base);
}
</style>

<script>
const CHAR_LENGTH = 28;

export default {
  name: 'WordGuess',
  props: {
    value: {
      type: String,
      default: '',
    },
    showAnswer: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    guess: '',
    oldGuess: '',
  }),
  computed: {
    current() {
      return this.$store.getters['rondo/current'] || {};
    },
    answer() {
      return (this.current && this.current.word) || '';
    },
    width() {
      return CHAR_LENGTH * this.answer.length;
    },
    clue() {
      const clue = this.$store.state.rondo.currentClues;
      return clue ? clue.map((c) => c.toUpperCase()) : null;
    },
  },
  methods: {
    type() {
      // Simulating watcher functionality.
      // Using real watchers will create an infinite loop, or unreadable code.
      this.guess = this.guess.toUpperCase();
      const oldVal = this.oldGuess;
      const newVal = this.guess;

      const isAdding = oldVal.length < newVal.length;
      const isDeleting = oldVal.length > newVal.length;

      const currentChar = this.$store.state.rondo.current.toUpperCase();
      const firstAppear = this.answer.toUpperCase().indexOf(currentChar);

      // User types char before clue char
      if (isAdding && newVal.length === firstAppear) {
        this.guess += currentChar;
      }

      // User types char before white space
      if (isAdding && this.answer[newVal.length] === ' ') {
        this.guess += ' ';
      }

      // User types char IN white space
      if (isAdding && this.answer[newVal.length - 1] === ' ') {
        const newChar = newVal.slice(-1);
        this.guess = `${oldVal} ${newChar}`;
      }

      // User removes char before a white space
      if (isDeleting && this.answer[oldVal.length - 2] === ' ') {
        this.guess = this.guess.slice(0, -1);
      }

      // User removes a white space
      if (isDeleting && this.answer[oldVal.length - 1] === ' ') {
        this.guess = this.guess.slice(0, -1);
      }

      if (this.clue) {
        const clueIndexes = this.clue.map((clue) => this.answer.indexOf(clue));

        // User types IN a revealed clue char
        const currentClueIndex = clueIndexes.indexOf(newVal.length - 1);
        if (isAdding && currentClueIndex !== -1) {
          this.guess = `${oldVal}${this.clue[currentClueIndex]}`;
        }

        // User type BEFORE a revealed clue char(s)
        let preClueIndex = clueIndexes.indexOf(newVal.length);
        while (isAdding && preClueIndex !== -1) {
          this.guess = `${this.guess}${this.clue[preClueIndex]}`;
          preClueIndex = clueIndexes.indexOf(this.guess.length);
        }
      }

      // User removes clue char
      if (isDeleting && newVal.length === firstAppear) {
        // console.log('User tryed to remove clue char');
        if (this.current.starts) {
          this.guess = oldVal;
        } else {
          this.guess = this.guess.slice(0, -1);
        }
      } else if (this.current.starts) {
        // User types before first char in a "start with" case
        const oldFirst = oldVal.charAt(0).toUpperCase();
        const newFirst = newVal.charAt(0).toUpperCase();

        if (oldFirst !== newFirst) {
          // console.log('User tryed to change clue char in "start with"');
          this.guess = oldVal;
        }
      }

      // After all proccessing, we can safely set the oldGuess...
      this.oldGuess = this.guess;

      // ...and emit sanitized value
      this.$emit('input', this.guess.toUpperCase());
    },
    prepareGuess() {
      // If answer "starts with", type the first character
      if (this.current.starts) {
        const char = this.answer.charAt(0).toUpperCase();
        this.guess = char;
        this.oldGuess = char;
        return true;
      }

      this.guess = '';
      this.oldGuess = '';
      return true;
    },
    guessChar(i) {
      if (this.showAnswer && !this.isClassMode) {
        return this.answer.charAt(i).toUpperCase();
      }

      if (this.answer[i] === ' ') {
        return ' ';
      }

      if (this.clue && this.clue.length > 0) {
        const clueIndexes = this.clue.map((clue) => this.answer.indexOf(clue));
        const clueIndex = clueIndexes.indexOf(i);

        if (clueIndex > -1) {
          return this.clue[clueIndex];
        }
      }

      if (!this.current.starts) {
        const currentChar = this.$store.state.rondo.current.toUpperCase();
        const firstAppear = this.answer.toUpperCase().indexOf(currentChar);

        if (i === firstAppear) {
          return currentChar;
        }
      }

      if (i >= this.guess.length) {
        return ' ';
      }

      return this.guess[i].toUpperCase();
    },
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.guess = this.value;
      },
    },
    answer() {
      this.prepareGuess();
    },
  },
  created() {
    this.prepareGuess();
  },
};
</script>
